import { P, usePermission } from "@crm/access/authorization/client";
import { runFrontendMutationWorkflow } from "@crm/effect/client/mutation-workflow";
import { ListPageShell, PageHeader } from "@crm/ui/components/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crm/ui/components/ui/alert-dialog";
import { Badge } from "@crm/ui/components/ui/badge";
import { Button } from "@crm/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { Input } from "@crm/ui/components/ui/input";
import { Label } from "@crm/ui/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@crm/ui/components/ui/table";
import { Textarea } from "@crm/ui/components/ui/textarea";
import { createFileRoute } from "@tanstack/react-router";
import { Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  useCreateKnowledgeTag,
  useDeleteKnowledgeTag,
  useUpdateKnowledgeTag,
} from "#app/knowledge/knowledge.mutations";
import {
  useKnowledgeEntryTags,
  useKnowledgeTags,
} from "#app/knowledge/knowledge.queries";
import { KnowledgeNav } from "#components/knowledge";

export const Route = createFileRoute("/_protected/knowledge/tags")({
  head: () => ({
  }),
  component: TagsPage,
});

type FormData = {
  name: string;
  keywords: string;
  color: string;
};

const defaultFormData: FormData = {
  name: "",
  keywords: "",
  color: "",
};

function TagsPage() {
  const canDelete = usePermission(P.KNOWLEDGE_TAG_DELETE_ALL);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteTag, setDeleteTag] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);

  // Mutations
  const createTagMutation = useCreateKnowledgeTag();
  const updateTagMutation = useUpdateKnowledgeTag();
  const deleteTagMutation = useDeleteKnowledgeTag();

  // 获取标签
  const tagsResult = useKnowledgeTags();
  const tags = AsyncResult.value(tagsResult).pipe(Option.getOrElse(() => []));

  // 获取条目-标签关联（用于统计使用次数）
  const entryTagsResult = useKnowledgeEntryTags();
  const entryTags = AsyncResult.value(entryTagsResult).pipe(Option.getOrElse(() => []));

  // 计算每个标签的使用次数
  const getTagUsageCount = (tagId: string) => entryTags.filter((et) => et.tagId === tagId).length;

  // 打开新建对话框
  const handleCreate = () => {
    setEditingTag(null);
    setFormData(defaultFormData);
    setIsDialogOpen(true);
  };

  // 打开编辑对话框
  const handleEdit = (tag: {
    id: string;
    name: string;
    keywords?: string[] | null;
    color?: string | null;
  }) => {
    setEditingTag({ id: tag.id, name: tag.name });
    setFormData({
      name: tag.name,
      keywords: (tag.keywords || []).join(", "),
      color: tag.color || "",
    });
    setIsDialogOpen(true);
  };

  // 保存标签
  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("标签名不能为空");
      return;
    }

    const keywords = formData.keywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);

    const targetTag = editingTag;
    const isEditing = Boolean(targetTag);

    return runFrontendMutationWorkflow({
      execute: () => {
        if (targetTag) {
          return updateTagMutation.executePromise({
            id: targetTag.id,
            changes: {
              name: formData.name,
              keywords,
              color: formData.color || null,
            },
          });
        }

        return createTagMutation.executePromise({
          name: formData.name,
          keywords,
          color: formData.color || null,
          sortOrder: tags.length,
        });
      },
      onSuccess: () => {
        if (isEditing) {
          toast.success("标签已更新");
        } else {
          toast.success("标签已创建");
        }
        setIsDialogOpen(false);
      },
      onFailure: () => {
        toast.error("操作失败");
      },
    });
  };

  // 删除标签
  const handleDelete = () => {
    const targetTag = deleteTag;
    if (!targetTag) {
      return;
    }

    return runFrontendMutationWorkflow({
      execute: () => deleteTagMutation.executePromise(targetTag.id),
      onSuccess: () => {
        toast.success("标签已删除");
        setDeleteTag(null);
      },
      onFailure: () => {
        toast.error("删除失败");
      },
    });
  };

  return (
    <ListPageShell>
      <KnowledgeNav />
      <div className="mt-4">
        <PageHeader
          action={{
            label: "新建标签",
            onClick: handleCreate,
            icon: <Plus className="h-4 w-4" />,
          }}
          description="管理知识库的分类标签"
          title="标签管理"
        />
      </div>

      <div className="mt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>标签名</TableHead>
              <TableHead>关键词</TableHead>
              <TableHead>使用次数</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell className="py-8 text-center text-muted-foreground" colSpan={4}>
                  暂无标签，点击「新建标签」创建
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell>
                    <Badge variant="secondary">{tag.name}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(tag.keywords || []).join(", ") || "-"}
                  </TableCell>
                  <TableCell>{getTagUsageCount(tag.id)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button onClick={() => handleEdit(tag)} size="sm" variant="ghost">
                        <Edit className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button
                          onClick={() => setDeleteTag({ id: tag.id, name: tag.name })}
                          size="sm"
                          variant="ghost"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 新建/编辑对话框 */}
      <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? "编辑标签" : "新建标签"}</DialogTitle>
            <DialogDescription>
              {editingTag ? "修改标签信息" : "创建新的知识库分类标签"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">标签名</Label>
              <Input
                id="name"
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例如：选品"
                value={formData.name}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywords">关键词（英文逗号分隔）</Label>
              <Textarea
                id="keywords"
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="例如：选品, 产品, 竞品, 利润"
                rows={3}
                value={formData.keywords}
              />
              <p className="text-muted-foreground text-xs">关键词用于未来的智能标签建议功能</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)} variant="outline">
              取消
            </Button>
            <Button onClick={handleSave}>{editingTag ? "保存" : "创建"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <AlertDialog onOpenChange={() => setDeleteTag(null)} open={!!deleteTag}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除标签「{deleteTag?.name}」吗？此操作不可恢复。
              关联此标签的文章将取消标签关联。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageShell>
  );
}
